import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isCompleted, isLive, STATUS_BADGE_TEXT } from '@/constants/matchStatus';
import { formatPlaceholderTeam } from '@/utils/placeholderTeam';
import { formatLiveMinute } from '@/utils/liveMinute';
import { formatPoints } from '@/utils/formatPoints';
import { useLiveTick } from '@/hooks/useLiveTick';
import styles from './styles.module.scss';

export interface MatchPreviewCardProps {
  matchId: number;
  /**
   * Tournament scope for the default link target
   * (`/tournaments/:tournamentId/matches/:matchId`). Optional so the card can
   * be reused outside a tournament (e.g. the league browser), where either an
   * explicit `to` is supplied or the card renders non-interactive.
   */
  tournamentId?: string;
  /**
   * Explicit link target, overriding the tournament-scoped default. When
   * neither `to` nor `tournamentId` is provided the card renders as a
   * non-interactive row.
   */
  to?: string;
  team1Name: string;
  team2Name: string;
  team1LogoUrl?: string;
  team2LogoUrl?: string;
  /**
   * Placeholder strings (e.g. "1A", "Winner R16-1") for unresolved
   * knockout slots whose actual team has not been determined yet. When the
   * real team name is missing/empty, the placeholder is rendered in the same
   * slot using identical typography (slot-identity-first: the placeholder
   * string represents the slot until its team is determined).
   */
  team1Placeholder?: string | null;
  team2Placeholder?: string | null;
  score1?: number | null;
  score2?: number | null;
  /** Match status: scheduled, live, finished, postponed, etc. */
  status: string;
  /** Match time string (e.g., "19:30") */
  time: string;
  venue?: string;
  /** Live-timing fields (populated by the polling job for in-play matches). */
  elapsedMinutes?: number | null;
  elapsedExtraMinutes?: number | null;
  elapsedUpdatedAt?: string | null;
  /**
   * Outcome coefficients — surfaced by the list endpoint only for predicted,
   * non-finished matches. Combined with `userPrediction.predicted_outcome`
   * the card renders the user's potential-points coefficient for the outcome
   * they predicted.
   */
  team1WinCoefficient?: number | null;
  drawCoefficient?: number | null;
  team2WinCoefficient?: number | null;
  userPrediction?: {
    predicted_score_team1: number;
    predicted_score_team2: number;
    predicted_outcome?: string;
    odds_score?: number | string | null;
    points_earned?: number | null;
  } | null;
  /**
   * `card` (default) — standalone elevated card. `row` — flat, divided row for
   * use inside a framed list (e.g. `CollapsibleGroup`): no radius/shadow/lift.
   */
  variant?: 'card' | 'row';
  /**
   * Optional context strip above the match row (e.g. the tournament name when
   * the same match is listed once per tournament). Part of the card surface,
   * so it shares the card's link target.
   */
  headerTitle?: string;
  /** Router `state` passed with the link navigation. */
  linkState?: unknown;
  testId?: string;
  className?: string;
}

// Treat `coeff <= 1` as "not set" — matches the canonical project rule used
// by `CoefficientsCard.isDefaultCoefficient` and `MatchDetailCard.isCoeffEmpty`.
// A real coefficient is always > 1 (book takes a margin); 1.00 is the
// placeholder some legacy code paths emit when there's no real value.
const formatCoeff = (raw: number | string | null | undefined): string | null => {
  if (raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  if (!Number.isFinite(n) || n <= 1) return null;
  return n.toFixed(2);
};

// DB stores `predicted_outcome` as one of `team1_win | draw | team2_win` only.
// `home_win` / `away_win` were never persisted (computed in-memory by
// `PointsCalculator.getOutcome` for points calculation but never written
// back). No defensive branches for those values.
const pickOutcomeCoefficient = (
  predictedOutcome: string | undefined,
  team1Win: number | null | undefined,
  draw: number | null | undefined,
  team2Win: number | null | undefined,
): number | null => {
  if (!predictedOutcome) return null;
  switch (predictedOutcome) {
    case 'team1_win':
      return team1Win ?? null;
    case 'team2_win':
      return team2Win ?? null;
    case 'draw':
      return draw ?? null;
    default:
      return null;
  }
};

/**
 * Compact match preview card. Single-row main layout:
 *   [team1 name + logo] · score/time · [logo + team2 name]
 * Optional context header strip above (`headerTitle`) and prediction subfooter
 * underneath when the user has predicted the match.
 * Venue + status are exposed via aria-label only (not visually rendered).
 */
export const MatchPreviewCard: React.FC<MatchPreviewCardProps> = ({
  matchId,
  tournamentId,
  to,
  team1Name,
  team2Name,
  team1LogoUrl,
  team2LogoUrl,
  team1Placeholder,
  team2Placeholder,
  score1,
  score2,
  status,
  time,
  venue,
  elapsedMinutes,
  elapsedExtraMinutes,
  elapsedUpdatedAt,
  team1WinCoefficient,
  drawCoefficient,
  team2WinCoefficient,
  userPrediction,
  variant = 'card',
  headerTitle,
  linkState,
  testId,
  className,
}) => {
  const { t } = useTranslation(['tournament']);
  const isFinished = isCompleted(status);
  const isLiveMatch = isLive(status);

  // Re-render every 30 s for live matches so the extrapolated minute advances
  // between 5-min polls. Pass `isLiveMatch` so non-live cards subscribe to
  // the no-op stub store inside `useLiveTick` and never reconcile on tick —
  // critical when MatchesTab mounts ~100 cards in its windowed list.
  const nowMs = useLiveTick(isLiveMatch);
  const liveMinuteText = isLiveMatch
    ? formatLiveMinute({
        status,
        elapsedMinutes,
        elapsedExtraMinutes,
        elapsedUpdatedAt,
        nowMs,
      })
    : null;
  const liveLabel = isLiveMatch ? (liveMinuteText ?? 'LIVE') : null;

  // Resolve display strings. Priority: real team name → placeholder string →
  // localised "TBD" fallback. Real names always win; placeholder is only shown
  // when the actual team has not yet been determined (knockout slot whose
  // bracket source hasn't resolved). Same typography weight/colour as real
  // names — DO NOT italicise / dim, per design contract.
  const trimmedTeam1 = team1Name?.trim() || '';
  const trimmedTeam2 = team2Name?.trim() || '';
  // Localise backend placeholder strings (e.g. "Winner Group A" → "Победитель
  // группы A" in RU). Unknown patterns fall back to the raw English string.
  const trimmedPlaceholder1 = formatPlaceholderTeam(team1Placeholder, t);
  const trimmedPlaceholder2 = formatPlaceholderTeam(team2Placeholder, t);
  const isPlaceholderTeam1 = !trimmedTeam1 && !!trimmedPlaceholder1;
  const isPlaceholderTeam2 = !trimmedTeam2 && !!trimmedPlaceholder2;
  const displayTeam1 = trimmedTeam1 || trimmedPlaceholder1 || t('tournament:tbd');
  const displayTeam2 = trimmedTeam2 || trimmedPlaceholder2 || t('tournament:tbd');

  // A match is considered "unresolved" (no useful detail page yet) when BOTH
  // teams are placeholders. When at least one team is resolved, the card stays
  // a proper link to the match-detail route.
  const isUnresolvedMatch = isPlaceholderTeam1 && isPlaceholderTeam2;

  // Hide the score for placeholder matches — there's nothing meaningful to
  // render and the kickoff time (known from match_date) is more useful.
  const hasPlaceholder = isPlaceholderTeam1 || isPlaceholderTeam2;
  const hasScore = !hasPlaceholder
    && score1 !== null && score1 !== undefined
    && score2 !== null && score2 !== undefined;

  // Compose aria-label so screen readers receive every field even when visual layout
  // would compress them (venue may ellipsis on narrow widths).
  const ariaParts: string[] = headerTitle ? [headerTitle, displayTeam1] : [displayTeam1];
  ariaParts.push(hasScore ? `${score1}:${score2}` : time);
  ariaParts.push(displayTeam2);
  if (venue) ariaParts.push(venue);
  ariaParts.push(STATUS_BADGE_TEXT[status] || status);
  const ariaLabel = ariaParts.filter(Boolean).join(', ');

  // Body content is identical between the resolved (link) and unresolved
  // (non-interactive) branches. Extracting it keeps the two wrappers thin.
  const body = (
    <>
      {headerTitle && (
        <div className={styles.header}>{headerTitle}</div>
      )}
      <div className={`${styles.main}${isLiveMatch ? ` ${styles.mainLive}` : ''}`}>
        {/* Live status chip: shows the current minute (e.g. "37'", "45+3'",
            "HT", "PEN") instead of the static "LIVE" label. Absolutely
            positioned on the leading edge of .main, vertically centered.
            aria-label on the <a> already announces the status. */}
        {liveLabel && (
          <span className={styles.liveIndicator} aria-hidden="true">{liveLabel}</span>
        )}
        {/* Team 1: name (right-aligned) + logo */}
        <div className={`${styles.team} ${styles.team1}`}>
          <h3 className={styles.teamName}>{displayTeam1}</h3>
          {team1LogoUrl && !isPlaceholderTeam1 ? (
            <img
              src={team1LogoUrl}
              alt={displayTeam1}
              className={styles.logo}
              // Intrinsic dimensions match the largest CSS variant (30×30 on ≥lg).
              // Reserving these on the element prevents a layout shift between
              // initial mount and image decode — without them, iOS WebKit
              // transiently shifts the .main row by 1-2px while the team logo
              // resolves, which in MatchesTab compounds with the lazy-load
              // prepend compensation and produces the pill "blink" symptom.
              // NOTE: do NOT add `loading="lazy"` — Chromium defers layout
              // calculation on lazy images and that re-introduces the shift
              // we are trying to eliminate. Cards above/below the viewport
              // are already not mounted (windowed list); browser-level lazy
              // loading on visible images would only hurt.
              width={30}
              height={30}
              decoding="async"
            />
          ) : (
            <div className={styles.logoPlaceholder} aria-hidden="true" />
          )}
        </div>

        {/* Center: score (only when both teams resolved and a score exists) or
            kickoff time. Placeholder matches always show kickoff time — score
            would be meaningless before teams are confirmed. */}
        <div className={styles.center}>
          {hasScore ? (
            <div className={styles.score}>
              <span>{score1}</span>
              <span className={styles.scoreSep}>:</span>
              <span>{score2}</span>
            </div>
          ) : (
            <div className={styles.kickoff}>{time}</div>
          )}
        </div>

        {/* Team 2: logo + name (left-aligned) */}
        <div className={`${styles.team} ${styles.team2}`}>
          {team2LogoUrl && !isPlaceholderTeam2 ? (
            <img
              src={team2LogoUrl}
              alt={displayTeam2}
              className={styles.logo}
              // See team1 logo above for the rationale (intrinsic w/h + async
              // decoding only; no loading="lazy" because Chromium defers
              // layout on lazy images).
              width={30}
              height={30}
              decoding="async"
            />
          ) : (
            <div className={styles.logoPlaceholder} aria-hidden="true" />
          )}
          <h3 className={styles.teamName}>{displayTeam2}</h3>
        </div>
      </div>

      {userPrediction && (() => {
        // Pre-finish: show the two potential-points coefficients (S = exact
        // score from the prediction's locked `odds_score`, R = outcome from
        // the live coefficient for the predicted side). Letter prefixes are
        // intentional — slash without labels forces recall ("which one was
        // first?"). One compact row, tabular numerals so values align
        // vertically across cards in the list.
        const scoreCoeff = isFinished ? null : formatCoeff(userPrediction.odds_score);
        const outcomeCoeffRaw = isFinished
          ? null
          : pickOutcomeCoefficient(
              userPrediction.predicted_outcome,
              team1WinCoefficient,
              drawCoefficient,
              team2WinCoefficient,
            );
        const outcomeCoeff = formatCoeff(outcomeCoeffRaw);
        const hasAnyCoefficient = !!scoreCoeff || !!outcomeCoeff;

        const potentialAriaLabel = hasAnyCoefficient
          ? t('tournament:potentialPointsAria', {
              score: scoreCoeff ?? '—',
              outcome: outcomeCoeff ?? '—',
            })
          : undefined;

        return (
          <div className={styles.predictionFooter} data-testid="match-card-prediction">
            <div className={styles.predictionInfo}>
              <span className={styles.predictionLabel}>{t('tournament:yourPrediction')}</span>
              <span className={styles.predictionScore}>
                {userPrediction.predicted_score_team1} : {userPrediction.predicted_score_team2}
              </span>
            </div>
            {isFinished && userPrediction.points_earned != null && (
              <div className={styles.predictionPoints} data-testid="match-card-points">
                {userPrediction.points_earned > 0 && (
                  <i className={`icon-chevrons-up ${styles.pointsPositive}`} />
                )}
                <span className="h6 label">
                  {userPrediction.points_earned > 0 ? '+' : ''}{formatPoints(userPrediction.points_earned)} pts
                </span>
              </div>
            )}
            {!isFinished && hasAnyCoefficient && (
              <div className={styles.potentialCoefficients} aria-label={potentialAriaLabel}>
                <span className={styles.coeffPair} aria-hidden="true">
                  <span className={styles.coeffPrefix}>{t('tournament:coeffPrefixScore')}</span>
                  <span className={styles.coeffValue}>{scoreCoeff ?? '—'}</span>
                </span>
                <span className={styles.coeffPair} aria-hidden="true">
                  <span className={styles.coeffPrefix}>{t('tournament:coeffPrefixResult')}</span>
                  <span className={styles.coeffValue}>{outcomeCoeff ?? '—'}</span>
                </span>
              </div>
            )}
          </div>
        );
      })()}
    </>
  );

  // Unresolved matches (both teams are placeholders) render as a plain <div>
  // — there's no useful match-detail page to navigate to yet. Visual chrome
  // (background, border-radius, shadow) is preserved via the same `.card`
  // class so the row looks identical to a resolved card; only the link
  // semantics are dropped.
  const cardClassName = [
    styles.card,
    variant === 'row' ? styles.row : null,
    className,
  ].filter(Boolean).join(' ');

  // Link target: explicit override wins, then the tournament-scoped route.
  // Without either there is nowhere to navigate, so the card falls back to
  // the same non-interactive branch used for unresolved matches.
  const linkTo = to ?? (tournamentId ? `/tournaments/${tournamentId}/matches/${matchId}` : null);

  if (isUnresolvedMatch || !linkTo) {
    return (
      <div
        className={cardClassName}
        role="group"
        aria-label={ariaLabel}
        data-match-id={matchId}
        data-testid={testId}
      >
        {body}
      </div>
    );
  }

  return (
    <Link
      to={linkTo}
      state={linkState}
      className={cardClassName}
      aria-label={ariaLabel}
      data-match-id={matchId}
      data-testid={testId}
    >
      {body}
    </Link>
  );
};

export default MatchPreviewCard;
